import {
  Button,
  Col,
  Divider,
  List,
  notification,
  Row,
  Table,
  UploadFile,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { DownloadCsvTemplateCard, ImportCsvDataCard } from 'components';
import { useState } from 'react';
import { IImportTagData, importTag } from 'services/tag';
import { readCSV } from 'utilities/file';
import { findDuplicate } from 'utilities/shared';

export const ImportTags: React.FC<{ redirection?: boolean }> = ({ redirection = true }) => {
  const [parsedCSVData, setParsedCSVData] = useState<IImportTagData[]>([]);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const { list } = useNavigation();

  const onFileUploadChange = async (info: UploadChangeParam<UploadFile<any>>) => {
    try {
      if (!info.fileList.length) {
        return;
      }

      let rowNumber = 0;
      const file = info.fileList[0].originFileObj!;
      const data: IImportTagData[] = await readCSV({
        file,
        transformFunction: (row: Record<string, any>) => {
          const columns = Object.keys(row).map((key) => key);

          rowNumber += 1;
          if (rowNumber === 1) {
            // throw error on missing required columns
            ['Name', 'Description'].forEach((requiredColumn, idx) => {
              const column = columns[idx];
              if (requiredColumn !== column) {
                throw new Error(
                  `Expected '${requiredColumn}' column at column ${idx + 1} but got '${column}'`,
                );
              }
            });
          }

          if (!row.Name) {
            throw new Error(`'Name' has missing values in row ${rowNumber}`);
          }

          const transformedRow = {
            name: row.Name,
            description: row.Description,
          };

          return transformedRow;
        },
      });

      const duplicateTagName = findDuplicate(data, 'name');
      const uniqueDuplicates = Array.from(new Set(duplicateTagName));
      if (duplicateTagName.length) {
        notification.error({
          message: 'Failed',
          description: `Found ${
            duplicateTagName.length
          } duplicate values for 'Name' - ${uniqueDuplicates.join(', ')}`,
        });
        return;
      }

      setParsedCSVData(data);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
  };

  const onFileRemove = (_file: UploadFile<any>) => {
    setParsedCSVData([]);
  };

  const onSubmitButtonClick = async () => {
    setIsButtonLoading(true);
    try {
      await importTag(parsedCSVData);
      if (redirection) list('tags');
      notification.success({
        message: 'Success',
        description: 'Successfully imported tags',
      });
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsButtonLoading(false);
  };

  const getCsvRowsForTemplate = () => {
    const rows = [['Name', 'Description']];
    const result = [
      {
        Name: 'Tag 100',
        Description: 'Tag description 100',
      },
      {
        Name: 'Tag 200',
        Description: 'Tag description 200',
      },
    ];
    result.forEach((elem) => {
      rows.push([elem.Name, elem.Description]);
    });

    return rows;
  };

  const flatParsedCsvData = parsedCSVData.map(({ name, description }) => {
    const flatObject: Record<string, string | undefined> = {
      key: name,
      name,
      description,
    };

    return flatObject;
  });

  const flatCsvObject = flatParsedCsvData[0] || {};
  const columns = [];
  for (const [key] of Object.entries(flatCsvObject)) {
    if (key === 'key') {
      continue;
    }
    columns.push({ title: key, key, dataIndex: key });
  }

  return (
    <List title="Import Tags">
      <Row gutter={[48, 16]} wrap style={{ marginBottom: '2rem' }}>
        <Col xs={11} lg={8}>
          <DownloadCsvTemplateCard
            rows={getCsvRowsForTemplate()}
            title="Download .csv template for import"
          />
        </Col>

        <Col xs={11} lg={8}>
          <ImportCsvDataCard onFileUploadChange={onFileUploadChange} onFileRemove={onFileRemove} />
        </Col>
      </Row>

      <Divider />
      {/* CSV Data Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <Table columns={columns} dataSource={flatParsedCsvData} />
        </Col>
      </Row>
      {/* Submit Button */}
      <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
        <Button
          style={{ width: '300px' }}
          loading={isButtonLoading}
          disabled={!parsedCSVData.length}
          onClick={onSubmitButtonClick}
        >
          Save
        </Button>
      </Row>
    </List>
  );
};
